import React, { useState } from 'react';
import classNames from 'classnames';

import css from './MobileCollapsible.module.css';

/**
 * Mobile-only collapsible. Renders a toggleable header on mobile (≤1023px)
 * and inlines its children directly on desktop (toggle hidden by CSS, body
 * always visible). The component is intentionally CSS-driven for the
 * platform split per the design doc §6.9 -- one render tree, no
 * useViewport gating, no SSR/hydration mismatch.
 *
 * The collapsed state is owned by `useState` so the toggle works on
 * mobile. SSR renders with `defaultOpen` as the initial state; clients
 * hydrate without re-rendering.
 *
 * For sections that already have their own heading (SectionAuthorMaybe,
 * SectionMapMaybe), the caller is responsible for hiding the inner
 * heading on mobile via its own CSS so the synthesized header doesn't
 * double up.
 */
const MobileCollapsible = ({
  header,
  defaultOpen = false,
  className,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div
      className={classNames(css.root, className)}
      data-open={isOpen ? 'true' : 'false'}
    >
      <button
        type="button"
        className={css.toggle}
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
      >
        <span className={css.header}>{header}</span>
        <span className={css.icon} aria-hidden="true">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      <div className={css.body}>{children}</div>
    </div>
  );
};

export default MobileCollapsible;
